// Typy okenního frameworku (window manager) — prezentační/UI stav mimo navigační URL stack.
//
// Pozn.: logický stav (které agendy jsou otevřené + jejich `stackId`) vlastní
// lineární navigační kernel (`shared/navigation`) a serializuje se do URL.
// Geometrie / režim okna / z-order jsou ephemerální UI stav a žijí zde.

import type { WindowKind } from '@/shared/navigation/core';

/** Identita floating stacku (skupiny osamostatněných oken). Plocha = bez stackId. */
export type StackId = string;

/** Klíč skupiny pro z-order: plocha má speciální klíč, floating skupiny svůj stackId. */
export const PLOCHA_STACK_KEY = '__plocha__';

/** Vrátí klíč skupiny (stack) pro dané (volitelné) stackId. */
export function stackKeyOf(stackId: string | undefined): string {
  return stackId ?? PLOCHA_STACK_KEY;
}

/** Režim okna v ploše/desktopu */
export type WindowMode = 'docked' | 'floating' | 'maximized' | 'minimized';

/** Geometrie plovoucího okna */
export interface WindowRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Vrátí rect upravený tak, aby byl celý uvnitř plochy (viewportu):
 *  - když je okno větší než plocha, zmenší se na velikost plochy,
 *  - posune se tak, aby bylo celé vidět (x ∈ [0, dim.w−w], y ∈ [0, dim.h−h]).
 * Při neznámé/nulové ploše vrací rect beze změny. (Použití: obnova z kotvy — okna celá vidět.)
 */
export function clampRectToViewport(rect: WindowRect, dim: { w: number; h: number }): WindowRect {
  if (dim.w <= 0 || dim.h <= 0) return rect;
  const w = Math.min(rect.w, dim.w);
  const h = Math.min(rect.h, dim.h);
  const x = Math.max(0, Math.min(rect.x, dim.w - w));
  const y = Math.max(0, Math.min(rect.y, dim.h - h));
  return { x, y, w, h };
}

/** Výška hlavičky (titulku) okna — drží se viditelná na ose Y. */
const TITLE_BAR_H = 58;
/** Minimální viditelná šířka okna na ose X (= 2× výška titulku). */
const MIN_VISIBLE_X = TITLE_BAR_H * 2;

/**
 * Volnější clamp pro POSUN okna: okno smí být částečně mimo plochu, ale nesmí zmizet celé.
 *  - osa Y: celá hlavička (titulek) zůstane vidět → y ∈ [0, dim.h − TITLE_BAR_H],
 *  - osa X: vždy aspoň 2× výška titulku vidět → x ∈ [MIN_VISIBLE_X − w, dim.w − MIN_VISIBLE_X].
 * Velikost NEmění (na rozdíl od clampRectToViewport). Při neznámé/nulové ploše vrací rect beze změny.
 */
export function clampRectKeepTitle(rect: WindowRect, dim: { w: number; h: number }): WindowRect {
  if (dim.w <= 0 || dim.h <= 0) return rect;
  const x = Math.max(MIN_VISIBLE_X - rect.w, Math.min(rect.x, dim.w - MIN_VISIBLE_X));
  const y = Math.max(0, Math.min(rect.y, dim.h - TITLE_BAR_H));
  return { x, y, w: rect.w, h: rect.h };
}

/** Krátká spring animace při osamostatnění / vrácení do plochy */
export type WindowAnim = 'detach' | 'dock' | null;

/** UI stav jednoho okna držený ve window-manager storu */
export interface WindowUiState {
  mode: WindowMode;
  /** Geometrie pro floating/maximized; docked okna se měří z plochy. */
  rect?: WindowRect;
  /** Předchozí geometrie (restore z maximize/minimize). */
  prevRect?: WindowRect;
  /** Předchozí režim (restore z minimize). */
  prevMode?: WindowMode;
  /** Probíhající spring animace (transientní). */
  anim?: WindowAnim;
}

/** Re-export pro pohodlí konzumentů projekce */
export type { WindowKind };
